import { createRoute, Link } from '@tanstack/react-router'
import { projectRoute } from './route'
import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Archive,
  ArchiveRestore,
  MessageSquare,
  MoreHorizontal,
  Pin,
  PinOff,
  Pencil,
  Rocket,
  Trash2,
  Search,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDateLocale } from '@/i18n/dates'
import { orpc } from '@/lib/orpc-client'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

export const chatsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/chats',
  component: AllChats,
})

type ChatThread = Awaited<ReturnType<typeof orpc.chat.listThreads>>['items'][number]

function nameInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

function AllChats() {
  const { t } = useTranslation('chat')
  const { intl } = useDateLocale()
  const { projectId } = chatsRoute.useParams()
  const [threads, setThreads] = useState<ChatThread[]>([])
  const [search, setSearch] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<ChatThread | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [renameTarget, setRenameTarget] = useState<ChatThread | null>(null)
  const [renameTitle, setRenameTitle] = useState('')
  const [isRenaming, setIsRenaming] = useState(false)
  const [filterUserId, setFilterUserId] = useState('')
  // Board 5q82: archived threads (the onboarding chat HUB archived) only show on request.
  const [showArchived, setShowArchived] = useState(false)

  const loadThreads = useCallback(() => {
    orpc.chat
      .listThreads({ projectId, includeArchived: showArchived })
      .then((res) => setThreads(res.items))
      .catch(() => {})
  }, [projectId, showArchived])

  useEffect(() => {
    loadThreads()
  }, [loadThreads])

  useEffect(() => {
    const handler = () => loadThreads()
    window.addEventListener('chat-threads-changed', handler)
    return () => window.removeEventListener('chat-threads-changed', handler)
  }, [loadThreads])

  const creators = useMemo(() => {
    const map = new Map<string, { name: string; avatarUrl: string | null }>()
    for (const t of threads) {
      if (t.createdBy && !map.has(t.createdBy.userId)) {
        map.set(t.createdBy.userId, {
          name: t.createdBy.name ?? t.createdBy.userId,
          avatarUrl: t.createdBy.avatarUrl,
        })
      }
    }
    return [...map.entries()].map(([userId, info]) => ({ userId, ...info }))
  }, [threads])

  const filtered = useMemo(() => {
    let result = threads
    if (filterUserId) {
      result = result.filter((t) => t.createdBy?.userId === filterUserId)
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      const untitled = t('chats.untitled')
      result = result.filter((th) => (th.title || untitled).toLowerCase().includes(q))
    }
    return result
  }, [threads, search, filterUserId, t])

  const pinned = useMemo(() => filtered.filter((t) => t.pinned), [filtered])
  const unpinned = useMemo(() => filtered.filter((t) => !t.pinned), [filtered])

  const handlePinThread = async (thread: ChatThread) => {
    try {
      await orpc.chat.updateThread({
        projectId,
        threadId: thread.id,
        pinned: !thread.pinned,
      })
      window.dispatchEvent(new CustomEvent('chat-threads-changed'))
    } catch {
      /* ignore */
    }
  }

  const handleUnarchiveThread = async (thread: ChatThread) => {
    try {
      await orpc.chat.updateThread({ projectId, threadId: thread.id, archived: false })
      window.dispatchEvent(new CustomEvent('chat-threads-changed'))
    } catch {
      /* ignore */
    }
  }

  const handleDeleteThread = async () => {
    if (!deleteTarget || isDeleting) return
    setIsDeleting(true)
    try {
      await orpc.chat.deleteThread({ projectId, threadId: deleteTarget.id })
      setDeleteTarget(null)
      window.dispatchEvent(new CustomEvent('chat-threads-changed'))
    } catch {
      /* ignore */
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRenameThread = async () => {
    if (!renameTarget || isRenaming || !renameTitle.trim()) return
    setIsRenaming(true)
    try {
      await orpc.chat.updateThread({
        projectId,
        threadId: renameTarget.id,
        title: renameTitle.trim(),
      })
      setRenameTarget(null)
      window.dispatchEvent(new CustomEvent('chat-threads-changed'))
    } catch {
      /* ignore */
    } finally {
      setIsRenaming(false)
    }
  }

  const renderThread = (thread: ChatThread) => (
    <div
      key={thread.id}
      className="flex items-center group rounded-lg hover:bg-muted/50 transition-colors"
    >
      <Link
        to="/$projectId/chat/$chatId"
        params={{ projectId, chatId: thread.id }}
        className="flex items-center gap-3 px-4 py-3 flex-1 min-w-0"
      >
        {thread.onboarding ? (
          <Rocket className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        ) : (
          <MessageSquare className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">
            {thread.title || t('chats.untitled')}
            {thread.archivedAt && (
              <span className="text-muted-foreground ml-2 rounded border px-1.5 py-0.5 text-[10px] font-normal">
                {t('chats.archived')}
              </span>
            )}
          </p>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span>
              {new Date(thread.lastMessageAt ?? thread.updatedAt).toLocaleDateString(intl)}
            </span>
            {thread.createdBy?.name && (
              <>
                <span>·</span>
                <Avatar className="h-4 w-4">
                  {thread.createdBy.avatarUrl && (
                    <AvatarImage src={thread.createdBy.avatarUrl} alt={thread.createdBy.name} />
                  )}
                  <AvatarFallback className="text-[8px]">
                    {nameInitials(thread.createdBy.name)}
                  </AvatarFallback>
                </Avatar>
                <span>{thread.createdBy.name}</span>
              </>
            )}
          </div>
        </div>
      </Link>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button className="p-2 rounded opacity-0 group-hover:opacity-100 hover:bg-muted text-muted-foreground hover:text-foreground transition-all shrink-0 mr-2">
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          {thread.archivedAt && (
            <DropdownMenuItem onClick={() => void handleUnarchiveThread(thread)}>
              <ArchiveRestore className="h-3.5 w-3.5 mr-2" />
              {t('chats.unarchive')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => void handlePinThread(thread)}>
            {thread.pinned ? (
              <>
                <PinOff className="h-3.5 w-3.5 mr-2" />
                {t('chats.unpin')}
              </>
            ) : (
              <>
                <Pin className="h-3.5 w-3.5 mr-2" />
                {t('chats.pin')}
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              setRenameTarget(thread)
              setRenameTitle(thread.title || '')
            }}
          >
            <Pencil className="h-3.5 w-3.5 mr-2" />
            {t('chats.rename')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => setDeleteTarget(thread)}
          >
            <Trash2 className="h-3.5 w-3.5 mr-2" />
            {t('chats.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )

  return (
    <div className="-mt-(--topbar-h) flex-1 overflow-y-auto p-6 pt-[calc(var(--topbar-h)+1.5rem)]">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{t('chats.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('chats.count', { count: threads.length })}
          </p>
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('chats.searchPlaceholder')}
              className="pl-9"
            />
          </div>
          {creators.length > 1 && (
            <Select
              value={filterUserId}
              onValueChange={(v) => setFilterUserId(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder={t('chats.all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('chats.all')}</SelectItem>
                {creators.map((c) => (
                  <SelectItem key={c.userId} value={c.userId}>
                    <span className="flex items-center gap-2">
                      <Avatar className="h-4 w-4">
                        {c.avatarUrl && <AvatarImage src={c.avatarUrl} alt={c.name} />}
                        <AvatarFallback className="text-[8px]">
                          {nameInitials(c.name)}
                        </AvatarFallback>
                      </Avatar>
                      {c.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            type="button"
            variant={showArchived ? 'secondary' : 'outline'}
            aria-pressed={showArchived}
            onClick={() => setShowArchived((v) => !v)}
          >
            <Archive className="h-4 w-4" />
            {t('chats.showArchived')}
          </Button>
        </div>

        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            {search ? t('chats.noMatch') : t('chats.empty')}
          </p>
        )}

        {pinned.length > 0 && (
          <div>
            <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 mb-1">
              {t('chats.pinned')}
            </h2>
            <div className="space-y-0.5">{pinned.map(renderThread)}</div>
          </div>
        )}

        {unpinned.length > 0 && (
          <div>
            {pinned.length > 0 && (
              <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 mb-1">
                {t('chats.recent')}
              </h2>
            )}
            <div className="space-y-0.5">{unpinned.map(renderThread)}</div>
          </div>
        )}
      </div>

      {/* Rename dialog */}
      <Dialog
        open={!!renameTarget}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('chats.renameTitle')}</DialogTitle>
            <DialogDescription>{t('chats.renameDescription')}</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void handleRenameThread()
            }}
          >
            <Input
              value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
              placeholder={t('chats.namePlaceholder')}
              autoFocus
            />
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameTarget(null)}
                disabled={isRenaming}
              >
                {t('chats.cancel')}
              </Button>
              <Button type="submit" disabled={isRenaming || !renameTitle.trim()}>
                {isRenaming ? t('chats.saving') : t('chats.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('chats.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('chats.deleteDescription', {
                title: deleteTarget?.title || t('chats.untitled'),
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              {t('chats.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteThread()}
              disabled={isDeleting}
            >
              {isDeleting ? t('chats.deleting') : t('chats.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
